"use client";

/**
 * @file Index Workerプロジェクトのソート可能なサイドバーコンポーネント
 * 統合基底コンポーネントを使用した軽量な実装
 */

import { type FragmentOf, readFragment } from "gql.tada";
import { IW_POLICIES } from "@/lib/authorization/policies/index-worker";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import { createDragOverlayProject } from "./drag-overlay-utils";
import { IndexWorkerProjectItemFragment } from "./fragments";
import { IwSidebarSortable } from "./iw-sidebar-sortable";
import ProjectSidebarItem from "./project-sidebar-item";
import { SortableSidebarGroupsBase } from "./sortable-sidebar-groups-base";
import type { IWProject } from "./types";

interface SortableIWProjectsProps {
  workspaceId: string;
  projectsData: readonly FragmentOf<typeof IndexWorkerProjectItemFragment>[];
  currentPathname: string;
}

/**
 * Index Workerプロジェクト用ソート可能サイドバーコンポーネント
 * 統合基底コンポーネントを使用してコードの重複を削減
 */
export default function IwSortableProjects({
  workspaceId,
  projectsData,
  currentPathname,
}: SortableIWProjectsProps) {
  const checkPolicy = useCheckPolicy();
  const canEditProjects = checkPolicy(IW_POLICIES["iw-project:edit"]).ok;

  // GraphQL Fragmentデータをプロジェクト型に変換
  const projects: IWProject[] = projectsData
    .map((fragmentData) => {
      const project = readFragment(
        IndexWorkerProjectItemFragment,
        fragmentData,
      );
      return project
        ? {
            id: project.id,
            name: project.name,
            ...(project.color && { color: project.color }),
          }
        : null;
    })
    .filter(
      (project): project is NonNullable<typeof project> => project !== null,
    );

  // IW専用のレンダリング関数
  const renderIWProject = (
    project: IWProject & { isProjectActive: boolean },
    isDragging: boolean,
  ) => {
    const pathPattern = `.*/${encodeURIComponent(workspaceId)}/iw/${encodeURIComponent(project.id)}/(board|list)`;
    const linkPath = `/${encodeURIComponent(workspaceId)}/iw/${encodeURIComponent(project.id)}`;

    // 対応するフラグメントデータを検索
    const fragmentData = projectsData.find((data) => {
      const p = readFragment(IndexWorkerProjectItemFragment, data);
      return p?.id === project.id;
    });

    // IW固有のサイドバーコンポーネント
    const sidebarContent = fragmentData ? (
      <IwSidebarSortable project={fragmentData} workspaceId={workspaceId} />
    ) : null;

    return (
      <ProjectSidebarItem
        projectId={project.id}
        projectName={project.name}
        projectColor={project.color || "#000000"}
        projectType="iw"
        workspaceId={workspaceId}
        isProjectActive={project.isProjectActive}
        isDragging={isDragging}
        linkHref={linkPath}
        activePattern={pathPattern}
      >
        {sidebarContent}
      </ProjectSidebarItem>
    );
  };

  // DragOverlay用のレンダリング関数
  const renderIWProjectForDragOverlay = (
    project: IWProject & { isProjectActive: boolean },
  ) => {
    // 対応するフラグメントデータを検索
    const fragmentData = projectsData.find((data) => {
      const p = readFragment(IndexWorkerProjectItemFragment, data);
      return p?.id === project.id;
    });

    // IW固有のサイドバーコンポーネント
    const sidebarContent = fragmentData ? (
      <IwSidebarSortable project={fragmentData} workspaceId={workspaceId} />
    ) : null;

    return createDragOverlayProject({
      projectId: project.id,
      projectName: project.name,
      projectColor: project.color || "#000000",
      projectType: "iw",
      workspaceId,
      children: sidebarContent,
    });
  };

  return (
    <SortableSidebarGroupsBase
      workspaceId={workspaceId}
      items={projects}
      projectType="iw"
      currentPathname={currentPathname}
      renderItem={renderIWProject}
      renderDragOverlayItem={renderIWProjectForDragOverlay}
      disableSorting={!canEditProjects}
    />
  );
}
